import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Textarea } from '../components/Textarea';
import { Badge } from '../components/Badge';

export const MSWLManagementPage = () => {
  // MSWL state
  const [mswlDescription, setMswlDescription] = useState(
    `I'm actively seeking literary fiction with strong character development and unique voices. I'm particularly drawn to contemporary stories that explore identity, family dynamics, and the immigrant experience.\n\nI love psychological thrillers with unreliable narrators and complex plot twists. Commercial women's fiction with depth and emotional resonance is always welcome.\n\nI'm building my science fiction list and would love to see near-future SF that explores social issues through a speculative lens.`
  );

  const [selectedGenres, setSelectedGenres] = useState<string[]>([
    'Literary Fiction',
    'Thriller',
    'Science Fiction',
    'Contemporary Fiction',
    'Women\'s Fiction',
  ]);

  const [specificWants, setSpecificWants] = useState(
    `• Stories featuring diverse protagonists and marginalized voices
• Character-driven narratives with strong emotional arcs
• Psychological depth and complex relationships
• Unique settings or time periods
• Fresh takes on classic themes
• Lyrical prose without being overly dense
• Stories that balance literary quality with commercial appeal
• Manuscripts that explore timely social issues
• Multiple POV narratives (when done well)
• Unreliable narrators and twisted plots`
  );

  const [dealBreakers, setDealBreakers] = useState(
    `• Poorly edited or unpolished manuscripts
• Excessive violence or graphic content without purpose
• Stereotypical or one-dimensional characters
• Derivative plots that don't bring anything new
• Romance as the primary plot driver (unless women's fiction)
• Fantasy with extensive world-building (not my genre)
• Young Adult or Middle Grade (outside my scope)
• Overly experimental or avant-garde prose
• Scripts, screenplays, or poetry collections
• Self-help or how-to books`
  );

  const [isPublished, setIsPublished] = useState(true);
  const [lastUpdated, setLastUpdated] = useState('2024-03-01');

  const availableGenres = [
    'Literary Fiction',
    'Commercial Fiction',
    'Contemporary Fiction',
    'Historical Fiction',
    'Thriller',
    'Mystery',
    'Suspense',
    'Psychological Thriller',
    'Science Fiction',
    'Fantasy',
    'Horror',
    'Women\'s Fiction',
    'Upmarket Fiction',
    'Book Club Fiction',
    'Memoir',
    'Narrative Nonfiction',
  ];

  const handleGenreToggle = (genre: string) => {
    if (selectedGenres.includes(genre)) {
      setSelectedGenres(selectedGenres.filter(g => g !== genre));
    } else {
      setSelectedGenres([...selectedGenres, genre]);
    }
  };

  const handleSave = () => {
    console.log('Saving MSWL:', {
      mswlDescription,
      selectedGenres,
      specificWants,
      dealBreakers,
      isPublished,
      lastUpdated: new Date().toISOString(),
    });
    setLastUpdated(new Date().toISOString().split('T')[0]);
    window.alert('MSWL saved successfully!');
  };

  const handlePublish = () => {
    if (selectedGenres.length === 0) {
      window.alert('Please select at least one genre');
      return;
    }
    if (!mswlDescription.trim()) {
      window.alert('Please provide an MSWL description');
      return;
    }

    handleSave();
    setIsPublished(true);
    window.alert('MSWL published successfully! Writers can now see your updated wishlist.');
  };

  const handleUnpublish = () => {
    if (window.confirm('Are you sure you want to unpublish your MSWL? Writers will not be able to see it until you publish again.')) {
      setIsPublished(false);
      window.alert('MSWL unpublished. It is now hidden from writers.');
    }
  };

  const handlePreview = () => {
    window.alert('Preview functionality would show how your MSWL appears to writers');
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '8px',
          }}>
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              color: theme.colors.textPrimary,
              margin: 0,
            }}>
              Manuscript Wish List
            </h1>
            <Badge variant={isPublished ? 'success' : 'warning'}>
              {isPublished ? 'Published' : 'Draft'}
            </Badge>
          </div>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Manage your MSWL to help writers understand what you're looking for
          </p>
          {lastUpdated && (
            <p style={{
              fontSize: '14px',
              color: theme.colors.textTertiary,
              marginTop: '8px',
            }}>
              Last updated: {new Date(lastUpdated).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Publication Status Info */}
        {!isPublished && (
          <Card style={{
            marginBottom: '24px',
            backgroundColor: theme.colors.warningLight,
            border: `1px solid ${theme.colors.warning}`,
          }}>
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start',
            }}>
              <div style={{ fontSize: '20px' }}>⚠️</div>
              <div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  color: theme.colors.textPrimary,
                  marginBottom: '4px',
                }}>
                  Your MSWL is currently unpublished
                </div>
                <div style={{
                  fontSize: '14px',
                  color: theme.colors.textSecondary,
                }}>
                  Writers cannot see your MSWL until you publish it. Make your changes and click "Publish MSWL" when ready.
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* MSWL Overview */}
        <Card style={{ marginBottom: '24px' }}>
          <Textarea
            label="MSWL Overview *"
            placeholder="Describe what you're looking for in a manuscript. Be specific about genres, themes, and styles that interest you..."
            value={mswlDescription}
            onChange={(e) => setMswlDescription(e.target.value)}
            rows={8}
            required
            helperText="This is the first thing writers will see. Make it engaging and informative!"
          />
          <div style={{
            marginTop: '8px',
            fontSize: '13px',
            color: theme.colors.textTertiary,
            textAlign: 'right',
          }}>
            {mswlDescription.length} / 2000 characters
          </div>
        </Card>

        {/* Genre Preferences */}
        <Card style={{ marginBottom: '24px' }}>
          <label style={{
            fontSize: '16px',
            fontWeight: '500',
            color: theme.colors.textPrimary,
            marginBottom: '12px',
            display: 'block',
          }}>
            Genre Preferences *
          </label>
          <p style={{
            fontSize: '14px',
            color: theme.colors.textSecondary,
            marginBottom: '16px',
          }}>
            Select all genres you're interested in representing. Writers will use these to find you.
          </p>

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
          }}>
            {availableGenres.map((genre) => {
              const isSelected = selectedGenres.includes(genre);
              return (
                <button
                  key={genre}
                  onClick={() => handleGenreToggle(genre)}
                  style={{
                    padding: '10px 20px',
                    border: `2px solid ${isSelected ? theme.colors.primary : theme.colors.border}`,
                    borderRadius: theme.borderRadius.pill,
                    backgroundColor: isSelected ? theme.colors.primaryLight : theme.colors.white,
                    color: isSelected ? theme.colors.primary : theme.colors.textSecondary,
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    fontFamily: theme.fonts.primary,
                  }}
                >
                  {isSelected && '✓ '}
                  {genre}
                </button>
              );
            })}
          </div>

          <div style={{
            marginTop: '12px',
            fontSize: '13px',
            color: theme.colors.textTertiary,
          }}>
            {selectedGenres.length} genre{selectedGenres.length !== 1 ? 's' : ''} selected
          </div>
        </Card>

        {/* Specific Wants */}
        <Card style={{ marginBottom: '24px' }}>
          <Textarea
            label="Specific Wants"
            placeholder="List specific elements, themes, or types of stories you're actively seeking..."
            value={specificWants}
            onChange={(e) => setSpecificWants(e.target.value)}
            rows={12}
            helperText="Be as specific as possible. Help writers understand exactly what excites you!"
          />
        </Card>

        {/* Deal Breakers */}
        <Card style={{ marginBottom: '24px' }}>
          <Textarea
            label="Deal Breakers"
            placeholder="List elements, themes, or genres you don't represent or are not interested in..."
            value={dealBreakers}
            onChange={(e) => setDealBreakers(e.target.value)}
            rows={10}
            helperText="Be clear about what you don't want to receive. This saves everyone time."
          />
        </Card>

        {/* MSWL Tips */}
        <Card style={{
          marginBottom: '32px',
          backgroundColor: theme.colors.primaryLight,
          border: `1px solid ${theme.colors.primary}`,
        }}>
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
          }}>
            <div style={{ fontSize: '20px' }}>💡</div>
            <div>
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: theme.colors.textPrimary,
                marginBottom: '8px',
              }}>
                Tips for a Great MSWL
              </div>
              <ul style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                margin: 0,
                paddingLeft: '20px',
                lineHeight: '1.6',
              }}>
                <li>Be specific about what you want and what you don't want</li>
                <li>Update your MSWL regularly to reflect current interests</li>
                <li>Mention specific comp titles or authors you admire</li>
                <li>Be honest about your capacity and response times</li>
                <li>Use positive language to describe what you're seeking</li>
                <li>Include examples of recent deals or sales when possible</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              variant="ghost"
              onClick={() => window.location.href = '/agent/profile/edit'}
            >
              ← Back to Profile
            </Button>
            <Button
              variant="outline"
              onClick={handlePreview}
            >
              Preview
            </Button>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              variant="outline"
              onClick={handleSave}
            >
              Save Draft
            </Button>
            {isPublished ? (
              <Button
                variant="outline"
                onClick={handleUnpublish}
              >
                Unpublish
              </Button>
            ) : (
              <Button
                onClick={handlePublish}
                disabled={selectedGenres.length === 0 || !mswlDescription.trim()}
              >
                Publish MSWL
              </Button>
            )}
          </div>
        </div>

        {/* Selected Genres Summary */}
        {selectedGenres.length > 0 && (
          <Card style={{ marginTop: '24px', backgroundColor: theme.colors.backgroundAlt }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              marginBottom: '12px',
            }}>
              Your Active Genres
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
            }}>
              {selectedGenres.map((genre) => (
                <Badge key={genre} variant="default">
                  {genre}
                </Badge>
              ))}
            </div>
          </Card>
        )}
      </main>

      <Footer />
    </div>
  );
};
